
<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">产品管理</h1>
            <p class="list-page-description">
              产品目录管理、产品配置与展示、价格体系配置、资料要求配置。产品更新会生成新版本，旧版本自动归档。
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <button
              v-permission="'product:add'"
              @click="openProductModal()"
              class="btn-primary inline-flex items-center gap-2"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              新建产品
            </button>
          </div>
        </div>
      </div>
    </template>
    <div class="min-h-full overflow-x-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
    <div class="relative overflow-hidden">
      <div class="pointer-events-none absolute -right-40 -top-24 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl"></div>
      <div class="pointer-events-none absolute left-10 top-36 h-40 w-40 rounded-full bg-slate-200/60 blur-3xl"></div>

      <div class="mx-auto w-full px-4 py-6">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div class="lg:col-span-3">
            <ProductCategorySidebar
              :categories="categories"
              :loading="categoryLoading"
              :selected-category-id="selectedCategory"
              title="产品类型管理"
              description="删除前会校验，类型下存在产品将无法删除。"
              @add="openCategoryModal()"
              @edit="openCategoryModal"
              @delete="(category) => confirmDelete('category', category)"
              @select="applyCategoryFilter"
            />
          </div>

          <div class="lg:col-span-9">
            <div class="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div class="border-b border-slate-200 p-5">
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h2 class="text-base font-semibold text-slate-900">产品列表</h2>
                    <p class="mt-1 text-xs text-slate-500">编辑将创建新版本，资料与价格模型将重新生成。</p>
                  </div>
                <div class="flex flex-wrap items-center gap-2"></div>
                </div>
                <PageFilterBar
                  shell-class="filter-bar-shell is-embedded"
                  body-class="filter-bar-body is-embedded"
                  content-class="flex flex-col gap-3"
                  :show-refresh="true"
                  :refresh-loading="productLoading"
                  @refresh="handleRefresh"
                >
                  <div class="filter-bar-field is-wide relative">
                    <input
                      v-model="searchKeyword"
                      type="text"
                      placeholder="搜索产品名称或编码"
                      class="form-search-input form-control-sm w-full"
                      @keyup.enter="handleSearch"
                    >
                    <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <select v-model="selectedCategory" class="form-select-native form-control-sm min-w-[160px]">
                    <option value="">全部类型</option>
                    <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
                  </select>
                  <select v-model="selectedStatus" class="form-select-native form-control-sm min-w-[160px]">
                    <option value="">全部状态</option>
                    <option value="1">上架</option>
                    <option value="0">下架</option>
                  </select>

                  <template #searchActions>
                    <button
                      type="button"
                      class="btn-primary"
                      :disabled="productLoading"
                      @click="handleSearch"
                    >
                      搜索
                    </button>
                    <button
                      type="button"
                      class="btn-secondary"
                      :disabled="productLoading"
                      @click="resetFilters"
                    >
                      重置
                    </button>
                  </template>
                </PageFilterBar>
              </div>

              <div class="data-table-shell">
                <div class="data-table-scroll">
                <table class="data-table text-sm">
                  <thead class="data-table-head">
                    <tr>
                      <th class="data-table-th text-left">产品名称</th>
                      <th class="data-table-th text-left">摘要</th>
                      <th class="data-table-th text-left">展示价</th>
                      <th class="data-table-th is-center">周期(天)</th>
                      <th class="data-table-th is-center">状态</th>
                      <th class="data-table-th is-center">热门</th>
                      <th class="data-table-th text-left">类型</th>
                      <th class="data-table-th is-center">操作</th>
                    </tr>
                  </thead>
                  <tbody class="data-table-body">
                    <!-- 骨架屏加载状态 -->
                    <template v-if="productLoading">
                      <tr v-for="n in 5" :key="n" class="animate-pulse">
                      <td class="data-table-td">
                        <div class="flex items-center gap-3">
                          <div class="w-10 h-10 bg-slate-200 rounded-lg"></div>
                          <div>
                            <div class="h-4 w-32 bg-slate-200 rounded mb-1"></div>
                            <div class="h-3 w-24 bg-slate-200 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"></div></td>
                      <td class="data-table-td"><div class="h-4 w-20 bg-slate-200 rounded"></div></td>
                      <td class="data-table-td is-center"><div class="h-4 w-12 bg-slate-200 rounded mx-auto"></div></td>
                      <td class="data-table-td is-center"><div class="h-6 w-14 bg-slate-200 rounded-full mx-auto"></div></td>
                      <td class="data-table-td is-center"><div class="h-6 w-10 bg-slate-200 rounded-full mx-auto"></div></td>
                      <td class="data-table-td"><div class="h-4 w-16 bg-slate-200 rounded"></div></td>
                      <td class="data-table-td">
                        <div class="flex items-center justify-center gap-2">
                          <div class="h-8 w-8 bg-slate-200 rounded"></div>
                          <div class="h-8 w-8 bg-slate-200 rounded"></div>
                          <div class="h-8 w-8 bg-slate-200 rounded"></div>
                        </div>
                      </td>
                    </tr>
                    </template>
                    <tr v-for="product in products" :key="product.id" class="data-table-row">
                      <td class="data-table-td">
                        <div class="flex items-center gap-3">
                          <div class="h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                            <img
                              v-if="product.icon"
                              :src="resolveFileUrl(product.icon)"
                              class="h-full w-full object-cover"
                              alt=""
                            >
                            <div v-else class="flex h-full w-full items-center justify-center text-slate-300">
                              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          </div>
                          <div>
                            <div class="data-table-cell-title">{{ product.name }}</div>
                            <div class="data-table-cell-meta">{{ product.shortName || product.code }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="data-table-td text-slate-600">{{ product.summary || '-' }}</td>
                      <td class="data-table-td font-medium text-primary-600">{{ product.priceDisplay || '-' }}</td>
                      <td class="data-table-td is-center text-slate-600">{{ product.durationDays ?? '-' }}</td>
                      <td class="data-table-td is-center">
                        <span
                          :class="[
                            'status-badge',
                            product.status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          ]"
                        >
                          {{ product.status === 1 ? '上架' : '下架' }}
                        </span>
                      </td>
                      <td class="data-table-td is-center">
                        <button
                          v-permission="'product:edit'"
                          @click="toggleHot(product)"
                          class="status-badge inline-flex items-center gap-1 transition-colors"
                          :class="product.isHot === 1 ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'"
                          :title="product.isHot === 1 ? '取消热门' : '设为热门'"
                        >
                          <svg class="h-3.5 w-3.5" :class="product.isHot === 1 ? 'fill-orange-500' : 'fill-none stroke-current'" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                          </svg>
                          {{ product.isHot === 1 ? '热门' : '-' }}
                        </button>
                      </td>
                      <td class="data-table-td text-slate-600">{{ categoryName(product.categoryId) }}</td>
                      <td class="data-table-td">
                        <div class="flex items-center justify-center gap-2">
                          <button
                            @click="openDetailModal(product.id)"
                            class="table-action-button is-primary"
                            title="查看详情"
                          >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </button>
                          <button
                            v-permission="'product:edit'"
                            @click="openProductModal(product.id)"
                            class="table-action-button is-primary"
                            title="编辑"
                          >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          <button
                            v-permission="'product:delete'"
                            @click="confirmDelete('product', product)"
                            class="table-action-button is-danger"
                            title="删除"
                          >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!productLoading && !products.length">
                      <td colspan="8" class="data-table-empty text-sm text-slate-500">暂无产品数据</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
              <AppPagination
                class="pagination-bridge"
                :pagination="pageInfo"
                :size="pageSize"
                :page-sizes="[10, 20, 50]"
                mode="simple"
                @page-change="loadProducts"
                @size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showCategoryModal" class="fixed inset-0 z-50">
      <div class="fixed inset-0 bg-black/50" @click="closeCategoryModal"></div>
      <div class="relative min-h-screen flex items-center justify-center px-6 py-8">
        <div class="w-full rounded-2xl bg-white shadow-2xl overflow-hidden max-w-4xl max-h-[90vh] flex flex-col">
          <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
            <h3 class="text-lg font-semibold text-slate-900">{{ isCategoryEdit ? '编辑产品类型' : '新增产品类型' }}</h3>
            <button @click="closeCategoryModal" class="rounded-full p-1 text-slate-400 hover:text-slate-600">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-5 overflow-y-auto flex-1">
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="text-sm font-medium text-slate-700">类型编码 <span class="text-red-500">*</span></label>
                <input v-model="categoryForm.code" class="form-input mt-2 w-full" placeholder="例如 FIN_TAX">
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">类型名称 <span class="text-red-500">*</span></label>
                <input v-model="categoryForm.name" class="form-input mt-2 w-full" placeholder="例如 财务税务">
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">执行部门</label>
                <div class="form-tree-panel max-h-56 overflow-auto mt-2">
                  <DeptTree
                    :tree-data="deptTree"
                    :selected-dept-id="categoryForm.deptId"
                    :expanded-ids="categoryModalExpandedIds"
                    @select="selectDeptForCategory"
                    @toggle="toggleCategoryModalDept"
                  />
                </div>
                <div class="mt-1 text-xs text-slate-500">
                  已选择：{{ categorySelectedDeptName || '未选择' }}
                </div>
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">图标</label>
                <div class="mt-2 flex items-center gap-4">
                  <div class="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                    <img
                      v-if="categoryIconPreview || categoryForm.icon"
                      :src="categoryIconPreview || resolveFileUrl(categoryForm.icon)"
                      class="h-full w-full object-cover"
                      alt="类型图标"
                    >
                    <div v-else class="flex h-full w-full items-center justify-center text-slate-400">
                      <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="cursor-pointer rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                      选择图标
                      <input type="file" accept="image/*" class="hidden" @change="handleIconSelect($event, 'category')">
                    </label>
                    <button
                      v-if="categoryIconPreview || categoryForm.icon"
                      type="button"
                      @click="clearIconSelect('category')"
                      class="text-left text-xs text-red-500 hover:text-red-600"
                    >
                      移除
                    </button>
                    <span class="text-xs text-slate-400">支持 JPG、PNG，≤5MB</span>
                  </div>
                </div>
              </div>
              <div>
                <label class="text-sm font-medium text-slate-700">描述</label>
                <textarea v-model="categoryForm.description" rows="3" class="form-textarea mt-2" placeholder="类型说明"></textarea>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-sm font-medium text-slate-700">排序</label>
                  <input v-model.number="categoryForm.sort" type="number" class="form-input mt-2 w-full">
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-700">状态</label>
                  <select v-model.number="categoryForm.status" class="form-select-native mt-2 w-full">
                    <option :value="1">启用</option>
                    <option :value="0">停用</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-200 px-6 py-4 shrink-0">
            <button @click="closeCategoryModal" class="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">取消</button>
            <button @click="submitCategory" class="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700">保存</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showProductModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/50" @click="closeProductModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ isProductEdit ? '编辑产品（生成新版本）' : '新建产品' }}</h3>
              <p class="text-xs text-slate-500">资料与价格模型将随版本变更重建。</p>
            </div>
            <button @click="closeProductModal" class="rounded-full p-1 text-slate-400 hover:text-slate-600">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="overflow-y-auto flex-1">
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 p-6">
              <div class="rounded-xl border border-slate-200 p-4">
                <h4 class="text-sm font-semibold text-slate-900">基础信息</h4>
                <div class="mt-3 space-y-3">
                  <div>
                    <label class="text-sm text-slate-700">产品名称 <span class="text-red-500">*</span></label>
                    <input v-model="productForm.name" class="form-input mt-2 w-full" placeholder="产品名称">
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">产品图标</label>
                    <div class="mt-2 flex items-center gap-4">
                      <div class="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                        <img
                          v-if="productIconPreview || productForm.icon"
                          :src="productIconPreview || resolveFileUrl(productForm.icon)"
                          class="h-full w-full object-cover"
                          alt="产品图标"
                        >
                        <div v-else class="flex h-full w-full items-center justify-center text-slate-400">
                          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                      <div class="flex flex-col gap-2">
                        <label class="cursor-pointer rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                          选择图标
                          <input type="file" accept="image/*" class="hidden" @change="handleIconSelect($event, 'product')">
                        </label>
                        <button
                          v-if="productIconPreview || productForm.icon"
                          type="button"
                          @click="clearIconSelect('product')"
                          class="text-left text-xs text-red-500 hover:text-red-600"
                        >
                          移除
                        </button>
                        <span class="text-xs text-slate-400">支持 JPG、PNG，≤5MB</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">产品编码 <span class="text-red-500">*</span></label>
                    <input v-model="productForm.code" class="form-input mt-2 w-full" placeholder="产品编码">
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">短名</label>
                    <input v-model="productForm.shortName" class="form-input mt-2 w-full" placeholder="短名">
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">产品类型 <span class="text-red-500">*</span></label>
                    <select v-model.number="productForm.categoryId" class="form-select-native mt-2 w-full">
                      <option value="">请选择</option>
                      <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">状态</label>
                    <select v-model.number="productForm.status" class="form-select-native mt-2 w-full">
                      <option :value="1">上架</option>
                      <option :value="0">下架</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">审批关联方案</label>
                    <select v-model.number="productForm.needApproval" class="form-select-native mt-2 w-full">
                      <option :value="1">需要审批</option>
                      <option :value="0">无需审批</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="rounded-xl border border-slate-200 p-4">
                <h4 class="text-sm font-semibold text-slate-900">版本信息</h4>
                <div class="mt-3 space-y-3">
                  <div>
                    <label class="text-sm text-slate-700">摘要</label>
                    <input v-model="productForm.version.summary" class="form-input mt-2 w-full" placeholder="摘要">
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">服务范围</label>
                    <input v-model="productForm.version.serviceScope" class="form-input mt-2 w-full" placeholder="适用对象/范围">
                  </div>
                  <div>
                    <div class="flex items-center justify-between">
                      <label class="text-sm text-slate-700">流程步骤</label>
                      <button
                        type="button"
                        @click="addProcessStep"
                        class="flex items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                      >
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        添加步骤
                      </button>
                    </div>
                    <div class="mt-3 space-y-3">
                      <div
                        v-for="(step, index) in productForm.version.processSteps"
                        :key="index"
                        class="group relative rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/50 p-4 shadow-sm transition-all hover:shadow-md"
                      >
                        <div class="flex items-start gap-3">
                          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-sm font-semibold text-white shadow-sm">
                            {{ index + 1 }}
                          </div>
                          <div class="flex-1 space-y-3">
                            <div>
                              <label class="text-xs font-medium text-slate-600">步骤名称 <span class="text-red-500">*</span></label>
                              <input
                                v-model="step.name"
                                class="form-input mt-1.5 w-full"
                                placeholder="例如：需求确认"
                              >
                            </div>
                            <div>
                              <label class="text-xs font-medium text-slate-600">注意事项</label>
                              <textarea
                                v-model="step.notes"
                                rows="2"
                                class="form-textarea mt-1.5 w-full"
                                placeholder="该步骤的注意事项说明"
                              ></textarea>
                            </div>
                          </div>
                          <button
                            type="button"
                            @click="removeProcessStep(index)"
                            class="shrink-0 rounded-lg p-1.5 text-slate-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                            title="移除步骤"
                          >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="!productForm.version.processSteps || !productForm.version.processSteps.length"
                        class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-8"
                      >
                        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                          <svg class="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        </div>
                        <p class="mt-3 text-sm font-medium text-slate-600">暂无流程步骤</p>
                        <p class="mt-1 text-xs text-slate-500">点击上方"添加步骤"按钮开始添加</p>
                      </div>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="text-sm text-slate-700">周期(天)</label>
                      <input v-model.number="productForm.version.durationDays" type="number" class="form-input mt-2 w-full">
                    </div>
                    <div>
                      <label class="text-sm text-slate-700">交付物</label>
                      <input v-model="productForm.version.deliverables" class="form-input mt-2 w-full" placeholder="交付物">
                    </div>
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">注意事项</label>
                    <input v-model="productForm.version.notices" class="form-input mt-2 w-full" placeholder="注意事项">
                  </div>
                  <div>
                    <label class="text-sm text-slate-700">详情介绍</label>
                    <RichTextEditor v-model="productForm.version.detailContent" upload-module="product" class="mt-2" />
                    <p class="mt-2 text-xs text-slate-500">支持富文本格式，保存后将作为产品详情介绍展示。</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 rounded-xl border border-slate-200 p-4">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-semibold text-slate-900">资料要求</h4>
                <button @click="addMaterial" class="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">新增资料</button>
              </div>
              <p class="mt-2 text-xs text-slate-500">资料类型从资料管理中获取，可配置必填。</p>
              <div class="mt-3 space-y-3">
                <div v-for="(material, index) in productForm.materials" :key="index" class="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 lg:grid-cols-6">
                  <div class="lg:col-span-3">
                    <label class="text-xs text-slate-500">资料类型</label>
                    <select
                      v-model.number="material.materialDefinitionId"
                      @change="syncMaterialInfo(material)"
                      :disabled="materialTypeLoading"
                      class="form-select-native form-control-sm mt-1 w-full disabled:bg-slate-100"
                    >
                      <option value="">请选择资料类型</option>
                      <optgroup
                        v-for="group in materialTypeGroups"
                        :key="group.categoryId"
                        :label="group.categoryName"
                      >
                        <option
                          v-for="type in group.definitions"
                          :key="getMaterialTypeId(type)"
                          :value="getMaterialTypeId(type)"
                        >
                          {{ getMaterialTypeName(type) }}
                        </option>
                      </optgroup>
                    </select>
                  </div>
                  <div class="lg:col-span-1">
                    <label class="text-xs text-slate-500">资料编码</label>
                    <div class="mt-2 text-xs text-slate-700">{{ material.code || '-' }}</div>
                  </div>
                  <div class="flex items-center gap-2 lg:col-span-1">
                    <input v-model="material.isRequired" type="checkbox" class="form-check">
                    <span class="text-xs text-slate-600">必填</span>
                  </div>
                  <div class="flex items-center gap-2 lg:col-span-1">
                    <span class="text-xs text-slate-600">{{ material.allowMultiple ? '可多份' : '单份' }}</span>
                  </div>
                  <div class="lg:col-span-6 flex justify-end">
                    <button @click="removeMaterial(index)" class="text-xs text-red-500 hover:text-red-600">移除</button>
                  </div>
                </div>
                <div v-if="!productForm.materials.length" class="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-500">
                  暂无资料要求
                </div>
              </div>
            </div>

            <div class="mt-6 rounded-xl border border-slate-200 p-4">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-semibold text-slate-900">价格模型</h4>
                <button @click="addPriceModel" class="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">新增模型</button>
              </div>
              <div class="mt-3 space-y-4">
                <div v-for="(model, modelIndex) in productForm.priceModels" :key="modelIndex" class="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div class="grid grid-cols-1 gap-3 lg:grid-cols-4">
                    <div>
                      <label class="text-xs text-slate-500">定价类型</label>
                      <select v-model="model.pricingType" @change="resetPriceItems(model)" class="form-select-native form-control-sm mt-1 w-full">
                        <option v-for="type in pricingTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
                      </select>
                    </div>
                    <div>
                      <label class="text-xs text-slate-500">币种</label>
                      <input v-model="model.currency" class="form-input form-control-sm mt-1 w-full" placeholder="CNY">
                    </div>
                    <div>
                      <label class="text-xs text-slate-500">单位</label>
                      <input v-model="model.unit" class="form-input form-control-sm mt-1 w-full" placeholder="次/年/人">
                    </div>
                    <div>
                      <label class="text-xs text-slate-500">展示价</label>
                      <input v-model="model.priceDisplay" class="form-input form-control-sm mt-1 w-full" placeholder="例如 3000元/次">
                    </div>
                  </div>

                  <div v-if="model.pricingType !== 'QUOTE'" class="mt-3 space-y-2">
                    <div v-for="(item, itemIndex) in model.items" :key="itemIndex" class="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-2 lg:grid-cols-6">
                      <div class="lg:col-span-2">
                        <label class="text-[11px] text-slate-500">名称</label>
                        <input v-model="item.name" class="form-input form-control-sm mt-1 w-full" placeholder="名称">
                      </div>
                      <div v-if="model.pricingType === 'TIER'" class="grid grid-cols-2 gap-2 lg:col-span-2">
                        <div>
                          <label class="text-[11px] text-slate-500">最小数量</label>
                          <input v-model.number="item.minQty" type="number" class="form-input form-control-sm mt-1 w-full">
                        </div>
                        <div>
                          <label class="text-[11px] text-slate-500">最大数量</label>
                          <input v-model.number="item.maxQty" type="number" class="form-input form-control-sm mt-1 w-full">
                        </div>
                      </div>
                      <div v-if="model.pricingType === 'RANGE'" class="grid grid-cols-2 gap-2 lg:col-span-2">
                        <div>
                          <label class="text-[11px] text-slate-500">最小价</label>
                          <input v-model.number="item.priceMin" type="number" class="form-input form-control-sm mt-1 w-full">
                        </div>
                        <div>
                          <label class="text-[11px] text-slate-500">最大价</label>
                          <input v-model.number="item.priceMax" type="number" class="form-input form-control-sm mt-1 w-full">
                        </div>
                      </div>
                      <div v-if="model.pricingType === 'FIXED' || model.pricingType === 'ITEMIZED' || model.pricingType === 'SUBSCRIPTION' || model.pricingType === 'TIER'" class="lg:col-span-2">
                        <label class="text-[11px] text-slate-500">价格</label>
                        <input v-model.number="item.price" type="number" class="form-input form-control-sm mt-1 w-full">
                      </div>
                      <div class="lg:col-span-2">
                        <label class="text-[11px] text-slate-500">说明</label>
                        <input v-model="item.note" class="form-input form-control-sm mt-1 w-full" placeholder="可选">
                      </div>
                      <div class="flex items-end justify-end lg:col-span-6">
                        <button @click="removePriceItem(model, itemIndex)" class="text-xs text-red-500 hover:text-red-600">移除</button>
                      </div>
                    </div>
                    <button @click="addPriceItem(model)" class="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">新增价格项</button>
                  </div>

                  <div v-else class="mt-3 rounded-lg border border-dashed border-slate-200 bg-white p-3 text-xs text-slate-500">
                    面议类型无需配置价格项。
                  </div>

                  <div class="mt-3 flex justify-end">
                    <button @click="removePriceModel(modelIndex)" class="text-xs text-red-500 hover:text-red-600">删除模型</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-200 px-6 py-4 shrink-0">
            <button @click="closeProductModal" class="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">取消</button>
            <button @click="submitProduct" class="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700">保存</button>
          </div>
        </div>
      </div>
    </div>
    <ProductDetailModal
      v-model="showDetailModal"
      :product-id="selectedDetailId"
      :categories="categories"
    />
    <div v-if="showConfirmModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/50" @click="closeConfirmModal"></div>
      <div class="relative mx-auto flex min-h-screen max-w-md items-center px-6 py-8">
        <div class="w-full rounded-2xl bg-white shadow-xl">
          <div class="border-b border-slate-200 px-6 py-4">
            <h3 class="text-lg font-semibold text-slate-900">确认删除</h3>
          </div>
          <div class="px-6 py-5 text-sm text-slate-600">
            {{ confirmMessage }}
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <button @click="closeConfirmModal" class="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">取消</button>
            <button @click="confirmDeleteAction" class="rounded-lg bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600">删除</button>
          </div>
        </div>
      </div>
    </div>
    </div>
  </ListPageShell>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import ProductCategorySidebar from '@/components/ProductCategorySidebar.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'
import ProductDetailModal from '@/components/ProductDetailModal.vue'
import {
  createProduct,
  createProductCategory,
  deleteProduct,
  deleteProductCategory,
  getCustomerMaterialTypes,
  getDeptTree,
  getProductCategories,
  getProductDetail,
  getProductPage,
  setProductHot,
  updateProduct,
  updateProductCategory
} from '@/api'
import { uploadFile } from '@/api/file'
import DeptTree from '@/views/system/components/DeptTree.vue'

const resolveFileUrl = (fileKey) => {
  if (!fileKey) return ''
  if (/^https?:\/\//i.test(fileKey)) return fileKey
  return `${process.env.VUE_APP_API_BASE_URL || ''}/${fileKey}`.replace(/\/+/g, '/').replace(':/', '://')
}

// 部门树（用于产品分类选择执行部门）
const deptTree = ref([])
const categoryModalExpandedIds = ref({})
const categorySelectedDeptName = ref('')

const loadDeptTree = async () => {
  try {
    const res = await getDeptTree()
    deptTree.value = res || []
    // 默认展开所有节点
    const expandAll = (list) => {
      list.forEach(item => {
        categoryModalExpandedIds.value[item.id] = true
        if (item.children?.length) {
          expandAll(item.children)
        }
      })
    }
    expandAll(deptTree.value)
  } catch (e) {
    console.error('加载部门失败', e)
  }
}

const toggleCategoryModalDept = (deptId) => {
  categoryModalExpandedIds.value[deptId] = !categoryModalExpandedIds.value[deptId]
}

const selectDeptForCategory = (dept) => {
  categoryForm.deptId = dept.id
  categorySelectedDeptName.value = dept.name
}

const findDeptById = (id, list = deptTree.value) => {
  if (!id) return null
  for (const item of list) {
    if (item.id === id) return item
    if (item.children?.length) {
      const found = findDeptById(id, item.children)
      if (found) return found
    }
  }
  return null
}

// 待上传的图标文件（选择后暂存，保存时再上传）
const categoryIconFile = ref(null)
const categoryIconPreview = ref('')
const categoryIconModified = ref(false)
const productIconFile = ref(null)
const productIconPreview = ref('')
const productIconModified = ref(false)

const handleIconSelect = (event, target) => {
  const file = event.target.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    event.target.value = ''
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    ElMessage.warning('图片大小不能超过5MB')
    event.target.value = ''
    return
  }
  if (target === 'category') {
    if (categoryIconPreview.value) URL.revokeObjectURL(categoryIconPreview.value)
    categoryIconFile.value = file
    categoryIconPreview.value = URL.createObjectURL(file)
    categoryIconModified.value = true
  } else {
    if (productIconPreview.value) URL.revokeObjectURL(productIconPreview.value)
    productIconFile.value = file
    productIconPreview.value = URL.createObjectURL(file)
    productIconModified.value = true
  }
  event.target.value = ''
}

const clearIconSelect = (target) => {
  if (target === 'category') {
    if (categoryIconPreview.value) URL.revokeObjectURL(categoryIconPreview.value)
    categoryIconFile.value = null
    categoryIconPreview.value = ''
    categoryForm.icon = ''
    categoryIconModified.value = true
  } else {
    if (productIconPreview.value) URL.revokeObjectURL(productIconPreview.value)
    productIconFile.value = null
    productIconPreview.value = ''
    productForm.icon = ''
    productIconModified.value = true
  }
}

const uploadIconIfNeeded = async (target) => {
  const file = target === 'category' ? categoryIconFile.value : productIconFile.value
  if (!file) return null
  const res = await uploadFile(file, 'product-icon', 'public')
  const fileKey = res?.fileName || res?.data?.fileName
  if (!fileKey) throw new Error('上传图标失败，未获取到图片地址')
  return fileKey
}

const categoryLoading = ref(false)
const productLoading = ref(false)
const detailLoading = ref(false)
const materialTypeLoading = ref(false)

const showCategoryModal = ref(false)
const isCategoryEdit = ref(false)
const showProductModal = ref(false)
const isProductEdit = ref(false)
const showDetailModal = ref(false)

const showConfirmModal = ref(false)
const confirmMessage = ref('')
const confirmPayload = ref({ type: '', id: null })

const categories = ref([])
const products = ref([])
const materialTypeGroups = ref([])
const materialTypeOptions = ref([])
const searchKeyword = ref('')
const selectedCategory = ref('')
const selectedStatus = ref('')
const pageSize = ref(20)
const pageInfo = reactive({
  current: 1,
  size: 20,
  total: 0,
  pages: 1,
  hasPrevious: false,
  hasNext: false
})

const categoryForm = reactive({
  id: null,
  code: '',
  name: '',
  icon: '',
  description: '',
  sort: 1,
  status: 1,
  deptId: null
})

const pricingTypes = [
  { label: '固定价', value: 'FIXED' },
  { label: '区间价', value: 'RANGE' },
  { label: '阶梯价', value: 'TIER' },
  { label: '计项价', value: 'ITEMIZED' },
  { label: '面议', value: 'QUOTE' },
  { label: '订阅价', value: 'SUBSCRIPTION' }
]

const getMaterialTypeId = (type) => type?.id ?? type?.materialDefinitionId
const getMaterialTypeName = (type) => type?.name || type?.nameAlias || '未命名'
const getMaterialTypeCode = (type) => type?.code || ''

const categoryName = (categoryId) => {
  const category = categories.value.find(item => item.id === categoryId)
  return category ? category.name : '-'
}

// 详情弹窗相关
const selectedDetailId = ref(null)
const openDetailModal = (id) => {
  selectedDetailId.value = id
  showDetailModal.value = true
}

const syncMaterialInfo = (material) => {
  const matchedType = materialTypeOptions.value.find(type => getMaterialTypeId(type) === material.materialDefinitionId)
  if (!matchedType) {
    material.name = material.name || ''
    material.code = material.code || ''
    return
  }
  material.name = getMaterialTypeName(matchedType)
  material.code = getMaterialTypeCode(matchedType)
  if (typeof matchedType.allowMultiple !== 'undefined') {
    material.allowMultiple = Boolean(matchedType.allowMultiple)
  }
}

const syncMaterialList = () => {
  productForm.materials.forEach(material => syncMaterialInfo(material))
}

const loadMaterialTypes = async () => {
  materialTypeLoading.value = true
  try {
    const data = await getCustomerMaterialTypes()
    materialTypeGroups.value = (data || []).map(group => ({
      categoryId: group.categoryId,
      categoryName: group.categoryName || group.categoryCode || '未分类',
      sort: group.sort ?? 0,
      definitions: (group.materialDefinitions || []).map(def => ({
        id: def.id,
        code: def.code,
        name: def.name,
        nameAlias: def.nameAlias,
        isRequired: def.isRequired,
        allowMultiple: def.allowMultiple,
        sort: def.sort ?? 0
      }))
    })).sort((a, b) => a.sort - b.sort)
    materialTypeOptions.value = materialTypeGroups.value.flatMap(group => group.definitions)
    syncMaterialList()
  } catch (error) {
    ElMessage.error(error.message || '加载资料类型失败')
  } finally {
    materialTypeLoading.value = false
  }
}

function parseProcessSteps(processSteps) {
  // 如果是数组，直接返回
  if (Array.isArray(processSteps)) {
    return processSteps.map((step, index) => ({
      sort: step.sort || index + 1,
      name: step.name || '',
      notes: step.notes || ''
    }))
  }
  // 如果是字符串，尝试解析为 JSON
  if (typeof processSteps === 'string' && processSteps.trim()) {
    try {
      const parsed = JSON.parse(processSteps)
      if (Array.isArray(parsed)) {
        return parsed.map((step, index) => ({
          sort: step.sort || index + 1,
          name: step.name || '',
          notes: step.notes || ''
        }))
      }
    } catch (error) {
      console.error('解析流程步骤 JSON 失败:', error)
    }
  }
  // 默认返回空数组
  return []
}

function createPriceModel(type) {
  const base = {
    pricingType: type,
    currency: 'CNY',
    unit: '次',
    priceDisplay: '',
    configJson: '',
    items: []
  }
  if (type === 'FIXED') {
    base.items = [{ name: '标准价', price: '', note: '' }]
  } else if (type === 'RANGE') {
    base.items = [{ name: '区间价', priceMin: '', priceMax: '', note: '' }]
  } else if (type === 'TIER') {
    base.items = [{ name: '阶梯价', minQty: '', maxQty: '', price: '', note: '' }]
  } else if (type === 'ITEMIZED') {
    base.items = [{ name: '基础项', price: '', note: '' }]
  } else if (type === 'SUBSCRIPTION') {
    base.items = [{ name: '首年', price: '', note: '' }, { name: '续费', price: '', note: '' }]
  }
  return base
}

const createEmptyProductForm = () => ({
  id: null,
  categoryId: '',
  code: '',
  name: '',
  shortName: '',
  icon: '',
  status: 1,
  needApproval: 1,
  version: {
    summary: '',
    detailContent: '',
    serviceScope: '',
    processSteps: [],
    durationDays: '',
    deliverables: '',
    notices: ''
  },
  materials: [],
  priceModels: [createPriceModel('FIXED')]
})

const productForm = reactive(createEmptyProductForm())
const loadCategories = async () => {
  categoryLoading.value = true
  try {
    categories.value = await getProductCategories()
  } catch (error) {
    ElMessage.error(error.message || '加载产品类型失败')
  } finally {
    categoryLoading.value = false
  }
}

const loadProducts = async (page = 1) => {
  productLoading.value = true
  try {
    const data = await getProductPage({
      categoryId: selectedCategory.value || undefined,
      keyword: searchKeyword.value || undefined,
      status: selectedStatus.value === '' ? undefined : Number(selectedStatus.value),
      page,
      size: pageSize.value
    })
    products.value = data.records || []
    Object.assign(pageInfo, {
      current: data.current || 1,
      size: data.size || pageSize.value,
      total: data.total || 0,
      pages: data.pages || 1,
      hasPrevious: data.hasPrevious,
      hasNext: data.hasNext
    })
  } catch (error) {
    ElMessage.error(error.message || '加载产品列表失败')
  } finally {
    productLoading.value = false
  }
}

const handlePageSizeChange = (size) => {
  pageSize.value = size
  pageInfo.size = size
  loadProducts(1)
}

const handleSearch = () => {
  loadProducts(1)
}

const handleRefresh = () => {
  loadProducts(pageInfo.current || 1)
}

const openCategoryModal = async (category) => {
  categoryIconFile.value = null
  if (categoryIconPreview.value) URL.revokeObjectURL(categoryIconPreview.value)
  categoryIconPreview.value = ''
  categoryIconModified.value = false
  await loadDeptTree()
  if (category) {
    Object.assign(categoryForm, category)
    categorySelectedDeptName.value = findDeptById(category.deptId)?.name || ''
    isCategoryEdit.value = true
  } else {
    Object.assign(categoryForm, {
      id: null,
      code: '',
      name: '',
      icon: '',
      description: '',
      sort: 1,
      status: 1,
      deptId: null
    })
    categorySelectedDeptName.value = ''
    isCategoryEdit.value = false
  }
  showCategoryModal.value = true
}

const closeCategoryModal = () => {
  showCategoryModal.value = false
}

const submitCategory = async () => {
  if (!categoryForm.code || !categoryForm.name) {
    ElMessage.warning('请填写类型编码和名称')
    return
  }
  try {
    // 如果有待上传的图标文件，先上传
    const iconKey = await uploadIconIfNeeded('category')
    if (iconKey) categoryForm.icon = iconKey

    const payload = { ...categoryForm }
    if (isCategoryEdit.value && !categoryIconModified.value) {
      delete payload.icon
    }

    if (isCategoryEdit.value) {
      await updateProductCategory(payload)
      ElMessage.success('类型更新成功')
    } else {
      await createProductCategory(payload)
      ElMessage.success('类型创建成功')
    }
    categoryIconFile.value = null
    if (categoryIconPreview.value) {
      URL.revokeObjectURL(categoryIconPreview.value)
      categoryIconPreview.value = ''
    }
    showCategoryModal.value = false
    await loadCategories()
  } catch (error) {
    ElMessage.error(error.message || '保存类型失败')
  }
}

const openProductModal = async (id) => {
  productIconFile.value = null
  if (productIconPreview.value) URL.revokeObjectURL(productIconPreview.value)
  productIconPreview.value = ''
  productIconModified.value = false
  if (!materialTypeOptions.value.length) {
    await loadMaterialTypes()
  }
  Object.assign(productForm, createEmptyProductForm())
  if (id) {
    isProductEdit.value = true
    await loadProductDetail(id, true)
  } else {
    isProductEdit.value = false
  }
  showProductModal.value = true
}

const closeProductModal = () => {
  showProductModal.value = false
}

const loadProductDetail = async (id, forEdit = false) => {
  detailLoading.value = true
  try {
    const data = await getProductDetail(id)
    if (forEdit) {
      Object.assign(productForm, {
        id: data.id,
        categoryId: data.categoryId,
        code: data.code,
        name: data.name,
        shortName: data.shortName || '',
        icon: data.icon || '',
        status: data.status ?? 1,
        needApproval: data.needApproval ?? 1,
        version: {
          summary: data.summary || '',
          detailContent: data.detailContent || '',
          serviceScope: data.serviceScope || '',
          processSteps: parseProcessSteps(data.processSteps),
          durationDays: data.durationDays || '',
          deliverables: data.deliverables || '',
          notices: data.notices || ''
        },
        materials: (data.materials || []).map(item => ({
          materialDefinitionId: item.materialDefinitionId,
          name: item.name || '',
          code: item.code || '',
          isRequired: item.isRequired === 1,
          allowMultiple: item.allowMultiple === 1
        })),
        priceModels: (data.priceModels || []).map(model => ({
          pricingType: model.pricingType,
          currency: model.currency || 'CNY',
          unit: model.unit || '',
          priceDisplay: model.priceDisplay || '',
          configJson: model.configJson || '',
          items: (model.items || []).map(item => ({
            name: item.name || '',
            minQty: item.minQty ?? '',
            maxQty: item.maxQty ?? '',
            price: item.price ?? '',
            priceMin: item.priceMin ?? '',
            priceMax: item.priceMax ?? '',
            note: item.note || ''
          }))
        }))
      })
      syncMaterialList()
    }
  } catch (error) {
    ElMessage.error(error.message || '加载产品详情失败')
  } finally {
    detailLoading.value = false
  }
}

const addMaterial = async () => {
  if (!materialTypeOptions.value.length) {
    await loadMaterialTypes()
  }
  productForm.materials.push({
    materialDefinitionId: '',
    name: '',
    code: '',
    isRequired: true,
    allowMultiple: false
  })
}

const removeMaterial = (index) => {
  productForm.materials.splice(index, 1)
}

const addProcessStep = () => {
  const newSort = productForm.version.processSteps.length + 1
  productForm.version.processSteps.push({
    sort: newSort,
    name: '',
    notes: ''
  })
}

const removeProcessStep = (index) => {
  productForm.version.processSteps.splice(index, 1)
  // 重新排序
  productForm.version.processSteps.forEach((step, idx) => {
    step.sort = idx + 1
  })
}

const addPriceModel = () => {
  productForm.priceModels.push(createPriceModel('FIXED'))
}

const removePriceModel = (index) => {
  productForm.priceModels.splice(index, 1)
}

const resetPriceItems = (model) => {
  const newModel = createPriceModel(model.pricingType)
  model.currency = newModel.currency
  model.unit = newModel.unit
  model.priceDisplay = ''
  model.items = newModel.items
}

const addPriceItem = (model) => {
  const template = createPriceModel(model.pricingType).items[0]
  model.items.push({ ...template })
}

const removePriceItem = (model, index) => {
  model.items.splice(index, 1)
}

const submitProduct = async () => {
  if (!productForm.name || !productForm.code || !productForm.categoryId) {
    ElMessage.warning('请填写产品名称、编码和类型')
    return
  }
  const missingMaterial = productForm.materials.find(item => !item.materialDefinitionId)
  if (missingMaterial) {
    ElMessage.warning('请选择资料类型')
    return
  }
  const missingStepName = productForm.version.processSteps.find(step => !step.name || !step.name.trim())
  if (missingStepName) {
    ElMessage.warning('请填写所有流程步骤的名称')
    return
  }

  // 将流程步骤转换为 JSON 字符串
  const processStepsArray = productForm.version.processSteps.map((step, index) => ({
    sort: step.sort || index + 1,
    name: step.name,
    notes: step.notes || ''
  }))
  const processStepsJson = processStepsArray.length > 0 ? JSON.stringify(processStepsArray) : ''

  try {
    // 如果有待上传的图标文件，先上传
    const iconKey = await uploadIconIfNeeded('product')
    if (iconKey) productForm.icon = iconKey

    const payload = {
      id: productForm.id || undefined,
      categoryId: Number(productForm.categoryId),
      code: productForm.code,
      name: productForm.name,
      shortName: productForm.shortName,
      ...(!isProductEdit.value || productIconModified.value ? { icon: productForm.icon || undefined } : {}),
      status: Number(productForm.status),
      needApproval: Number(productForm.needApproval),
      version: {
        ...productForm.version,
        durationDays: productForm.version.durationDays ? Number(productForm.version.durationDays) : undefined,
        processSteps: processStepsJson
      },
      materials: productForm.materials.map((item, index) => ({
        materialDefinitionId: Number(item.materialDefinitionId),
        isRequired: item.isRequired ? 1 : 0,
        sort: index + 1
      })),
      priceModels: productForm.priceModels.map((model, index) => ({
        pricingType: model.pricingType,
        currency: model.currency || 'CNY',
        unit: model.unit || '',
        priceDisplay: model.priceDisplay || '',
        configJson: model.configJson || null,
        sort: index + 1,
        items: (model.items || []).map((item, itemIndex) => ({
          name: item.name || '',
          minQty: item.minQty !== '' ? Number(item.minQty) : undefined,
          maxQty: item.maxQty !== '' ? Number(item.maxQty) : undefined,
          price: item.price !== '' ? Number(item.price) : undefined,
          priceMin: item.priceMin !== '' ? Number(item.priceMin) : undefined,
          priceMax: item.priceMax !== '' ? Number(item.priceMax) : undefined,
          note: item.note || '',
          sort: itemIndex + 1
        }))
      }))
    }

    if (isProductEdit.value) {
      await updateProduct(payload)
      ElMessage.success('产品已更新并生成新版本')
    } else {
      await createProduct(payload)
      ElMessage.success('产品创建成功')
    }
    productIconFile.value = null
    if (productIconPreview.value) {
      URL.revokeObjectURL(productIconPreview.value)
      productIconPreview.value = ''
    }
    showProductModal.value = false
    await loadProducts(1)
  } catch (error) {
    ElMessage.error(error.message || '保存产品失败')
  }
}

const toggleHot = async (product) => {
  const newHot = product.isHot === 1 ? 0 : 1
  try {
    await setProductHot({ id: product.id, isHot: newHot })
    product.isHot = newHot
    ElMessage.success(newHot === 1 ? '已设为热门' : '已取消热门')
  } catch (error) {
    ElMessage.error(error.message || '设置热门状态失败')
  }
}

const confirmDelete = (type, data) => {
  confirmPayload.value = { type, id: data.id }
  confirmMessage.value = type === 'category'
    ? `确认删除类型「${data.name}」？若类型下存在产品将无法删除。`
    : `确认删除产品「${data.name}」？`
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
}

const confirmDeleteAction = async () => {
  const { type, id } = confirmPayload.value
  try {
    if (type === 'category') {
      await deleteProductCategory(id)
      ElMessage.success('类型已删除')
      await loadCategories()
    } else if (type === 'product') {
      await deleteProduct(id)
      ElMessage.success('产品已删除')
      await loadProducts(pageInfo.current)
    }
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  } finally {
    showConfirmModal.value = false
  }
}

const resetFilters = () => {
  searchKeyword.value = ''
  selectedCategory.value = ''
  selectedStatus.value = ''
  loadProducts(1)
}

const applyCategoryFilter = async (categoryId) => {
  const nextValue = String(categoryId)
  selectedCategory.value = selectedCategory.value === nextValue ? '' : nextValue
  await loadProducts(1)
}

onMounted(async () => {
  await loadCategories()
  await loadMaterialTypes()
  await loadProducts(1)
})
</script>
